import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { LocalConfig } from 'utils';

import { SportManager, useApplicationState, useSettings } from '@atlas/core';

import useAnimated from './useAnimated';

const INCLUDED_TRANSLATES = {
  BR_PT: 'pt-br',
  DEU: 'de',
  ENG: 'en',
  RUS: 'ru',
};
const WIDGETS_ORIGIN = 'https://matchtracker.live';

export const LiveCenterWidget = ({ fixed, widgetsData }) => {
  const { id, widgets } = widgetsData;

  const [liveCenterUrl, setLiveCenterUrl] = useState('');
  const { lang, theme } = useSettings();
  const liveCenterLang = INCLUDED_TRANSLATES[lang] || 'en';
  const animated = useAnimated(fixed, 100);
  const widgetsFrame = React.useRef(null);
  const scoreBoard = widgets?.find(widget => widget.type === 'SCORE_BOARD');
  const [frameLoaded, setFrameLoaded] = useState(false);
  const projectId = LocalConfig.get('projectId');
  const [messageData, setMessageData] = useState({});
  const { authenticated } = useApplicationState();

  const currentTheme = useMemo(() => {
    const styles = getComputedStyle(document.body || null);
    if (!theme) {
      return {};
    }

    return window.btoa(
      JSON.stringify({
        branding: {
          background: styles.getPropertyValue('--material'),
          logo: 'https://site.com/logo.png',
        },
        colors: {
          background: styles.getPropertyValue('--material'),
          border: styles.getPropertyValue('--border'),
          icon: styles.getPropertyValue('--icon'),
          link: styles.getPropertyValue('--link'),
          primary: styles.getPropertyValue('--primary'),
          score: styles.getPropertyValue('--amber'),
          subtext: styles.getPropertyValue('--sub-text'),
          text: styles.getPropertyValue('--text-for-material'),
        },
      }),
    );
  }, [theme]);

  const liveCenterWidgetData = useMemo(() => {
    const liveCenterData = {
      eventId: id,
      params: {
        clientID: [Number(projectId)],
        lang: [liveCenterLang],
        login: [Number(authenticated)],
        mode: [0],
        timestamp: [Date.now()],
      },
      widgetType: 'SCORE_BOARD',
    };

    return {
      liveCenterData,
    };
  }, [id, projectId, liveCenterLang, authenticated]);

  useEffect(() => {
    SportManager.getWidgetUrl(liveCenterWidgetData.liveCenterData)
      .then(response => {
        setLiveCenterUrl(response.url);
      })
      .catch(error => {
        console.error(error);
      });
  }, [liveCenterWidgetData.liveCenterData]);

  useEffect(() => {
    const getWidgetsData = event => {
      if (event.origin === WIDGETS_ORIGIN) {
        setFrameLoaded(true);
        setMessageData(JSON.parse(event.data));
      }
    };

    window.addEventListener('message', getWidgetsData);

    return () => {
      window.removeEventListener('message', getWidgetsData);
    };
  }, []);

  useEffect(() => {
    if (!liveCenterUrl || !widgetsFrame.current?.contentWindow) return;

    const data = {
      text: JSON.stringify({
        params: {
          eventId: scoreBoard?.widgetId,
          lang: liveCenterLang,
          login: Number(authenticated),
          mode: messageData.mode || 0,
          theme: currentTheme,
        },
      }),
      timestamp: Date.now(),
    };

    try {
      widgetsFrame.current.contentWindow.postMessage(data, '*');
    } catch (error) {
      console.error('Failed to post message:', error);
    }
  }, [
    messageData.mode,
    liveCenterUrl,
    currentTheme,
    id,
    scoreBoard?.widgetId,
    frameLoaded,
    liveCenterLang,
    authenticated,
  ]);

  useEffect(() => {
    widgetsFrame.current.style.height = `${Math.ceil(messageData.height)}px`;
  }, [messageData.height]);

  const isFixed = messageData.mode > 0 && fixed;

  if (!id && !widgets) {
    return null;
  }

  return (
    <div className={cx('live-tab', 'live-center-tab')}>
      <div className={cx('live-center-block', { animated, fixed: isFixed })}>
        <iframe
          allowFullScreen={true}
          height="100%"
          ref={widgetsFrame}
          src={liveCenterUrl}
          width="100%"
        />
      </div>
    </div>
  );
};
